import { useEffect, useRef, useState } from 'react';
import type {
  PublicVendorSearchResult,
  QuestionnaireAnswerValue,
  QuestionnaireQuestionConfig,
  QuestionnaireQuestionType,
} from '@peles/shared';

import { GoogleAddressAutocomplete } from '../common/GoogleAddressAutocomplete';
import { PhoneInput } from '../common/PhoneInput';

const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

export interface QuestionInputProps {
  questionType: QuestionnaireQuestionType;
  config: QuestionnaireQuestionConfig;
  value: QuestionnaireAnswerValue;
  vendorId?: string | null;
  disabled?: boolean;
  onChange: (value: QuestionnaireAnswerValue, vendorId?: string | null) => void;
  /** VENDOR questions: search the producer's vendor list (pick or free text). */
  searchVendors?: (query: string) => Promise<PublicVendorSearchResult[]>;
}

/**
 * Renders the input control for a questionnaire question. Generic — used by
 * the public fill page; parameterized by type + config, never by domain.
 */
export function QuestionInput({
  questionType,
  config,
  value,
  vendorId,
  disabled,
  onChange,
  searchVendors,
}: QuestionInputProps) {
  switch (questionType) {
    case 'TEXT':
      return (
        <input
          type="text"
          className={INPUT_CLASS}
          value={value.text ?? ''}
          placeholder={config.placeholder}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, text: e.target.value })}
        />
      );
    case 'ADDRESS':
      return (
        <GoogleAddressAutocomplete
          value={value.text ?? ''}
          placeholder={config.placeholder}
          disabled={disabled}
          onChange={(text) => onChange({ ...value, text })}
          onAddressSelect={(fields) => onChange({ ...value, text: fields.formattedAddress })}
          className={INPUT_CLASS}
        />
      );
    case 'LONG_TEXT':
      return (
        <textarea
          rows={3}
          className={INPUT_CLASS}
          value={value.text ?? ''}
          placeholder={config.placeholder}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, text: e.target.value })}
        />
      );
    case 'NUMBER':
      return (
        <input
          type="number"
          className={`${INPUT_CLASS} max-w-[10rem]`}
          value={value.number ?? ''}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              ...value,
              number: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        />
      );
    case 'DATE':
      return (
        <input
          type="date"
          className={`${INPUT_CLASS} max-w-[12rem]`}
          value={value.date ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, date: e.target.value })}
        />
      );
    case 'TIME':
      return (
        <input
          type="time"
          className={`${INPUT_CLASS} max-w-[10rem]`}
          value={value.time ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, time: e.target.value })}
        />
      );
    case 'YES_NO':
      return (
        <div className="flex gap-2">
          {[
            { label: 'Yes', bool: true },
            { label: 'No', bool: false },
          ].map((option) => (
            <button
              key={option.label}
              type="button"
              disabled={disabled}
              onClick={() =>
                onChange({
                  ...value,
                  bool: value.bool === option.bool ? undefined : option.bool,
                })
              }
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                value.bool === option.bool
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      );
    case 'SINGLE_CHOICE':
      return (
        <div className="space-y-1.5">
          {(config.choices ?? []).map((choice) => (
            <label key={choice} className="flex items-start gap-2 text-sm text-gray-800">
              <input
                type="radio"
                className="mt-0.5"
                checked={value.choice === choice}
                disabled={disabled}
                onChange={() => onChange({ ...value, choice, otherText: undefined })}
              />
              <span>{choice}</span>
            </label>
          ))}
          {config.allowOther && (
            <label className="flex items-start gap-2 text-sm text-gray-800">
              <input
                type="radio"
                className="mt-0.5"
                checked={value.choice === '__other__'}
                disabled={disabled}
                onChange={() => onChange({ ...value, choice: '__other__' })}
              />
              <span className="flex-1">
                Other:
                <input
                  type="text"
                  className={`${INPUT_CLASS} mt-1`}
                  value={value.otherText ?? ''}
                  disabled={disabled || value.choice !== '__other__'}
                  onChange={(e) => onChange({ ...value, otherText: e.target.value })}
                />
              </span>
            </label>
          )}
        </div>
      );
    case 'CONTACT':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            type="text"
            className={INPUT_CLASS}
            placeholder="Name"
            value={value.contactName ?? ''}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, contactName: e.target.value })}
          />
          <PhoneInput
            className={INPUT_CLASS}
            placeholder="Phone number"
            value={value.contactPhone ?? ''}
            disabled={disabled}
            onChange={(contactPhone) => onChange({ ...value, contactPhone })}
          />
        </div>
      );
    case 'VENDOR':
      return (
        <VendorPickOrTypeInput
          value={value}
          vendorId={vendorId ?? null}
          disabled={disabled}
          onChange={onChange}
          searchVendors={searchVendors}
        />
      );
  }
}

/**
 * Pick-from-list or free-text vendor input (same UX as expenses): selecting a
 * suggestion links the vendor; typing keeps plain text and clears the link.
 * Clients can never create a vendor from here.
 */
function VendorPickOrTypeInput({
  value,
  vendorId,
  disabled,
  onChange,
  searchVendors,
}: {
  value: QuestionnaireAnswerValue;
  vendorId: string | null;
  disabled?: boolean;
  onChange: (value: QuestionnaireAnswerValue, vendorId?: string | null) => void;
  searchVendors?: (query: string) => Promise<PublicVendorSearchResult[]>;
}) {
  const [suggestions, setSuggestions] = useState<PublicVendorSearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const text = value.vendorName ?? '';

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurRef.current) clearTimeout(blurRef.current);
    };
  }, []);

  function handleTextChange(next: string) {
    onChange({ ...value, vendorName: next }, null);
    if (!searchVendors || next.trim().length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchVendors(next.trim())
        .then((results) => {
          setSuggestions(results);
          setIsOpen(results.length > 0);
        })
        .catch(() => {
          setSuggestions([]);
          setIsOpen(false);
        });
    }, 250);
  }

  return (
    <div className="relative">
      <input
        type="text"
        className={INPUT_CLASS}
        placeholder="Type a name, or pick from our vendor list"
        value={text}
        disabled={disabled}
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls="questionnaire-vendor-suggestions"
        autoComplete="off"
        onChange={(e) => handleTextChange(e.target.value)}
        onBlur={() => {
          blurRef.current = setTimeout(() => setIsOpen(false), 200);
        }}
        onFocus={() => {
          if (blurRef.current) clearTimeout(blurRef.current);
          if (suggestions.length > 0 && text.trim().length >= 2) setIsOpen(true);
        }}
      />
      {vendorId && <p className="mt-1 text-xs text-green-700">Linked to our vendor list ✓</p>}
      {isOpen && (
        <ul
          id="questionnaire-vendor-suggestions"
          role="listbox"
          className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-auto"
        >
          {suggestions.map((vendor) => (
            <li
              key={vendor.id}
              role="option"
              aria-selected={vendor.id === vendorId}
              className="px-3 py-2 cursor-pointer text-sm hover:bg-gray-50 text-gray-900"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange({ ...value, vendorName: vendor.businessName }, vendor.id);
                setIsOpen(false);
              }}
            >
              <span className="font-medium">{vendor.businessName}</span>
              {vendor.city && <span className="text-xs text-gray-500 ml-2">{vendor.city}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
